'use client'

import * as React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { saveA11yCookie } from '@/actions/save-a11y-cookie'
import { applySettingsToElement } from '@/lib/a11y/apply-settings'
import { detectPreferredSettings } from '@/lib/a11y/prefers'
import { readSettingsFromStorage, writeSettingsToStorage } from '@/lib/a11y/storage'
import {
  A11Y_STORAGE_KEY,
  DEFAULT_ACCESSIBILITY_SETTINGS,
  type AccessibilitySettings,
} from '@/lib/a11y/types'

interface AccessibilityContextValue {
  settings: AccessibilitySettings
  update: (partial: Partial<AccessibilitySettings>) => void
  reset: () => void
  announce: (message: string) => void
  /**
   * Da li je korisnik prijavljen. Postavke se čuvaju na uređaju u svakom slučaju
   * (localStorage + cookie); samo prijavljenima idu i na nalog, pa ih prate na
   * svim uređajima.
   */
  authenticated: boolean
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null)

interface ProviderProps {
  initialSettings: AccessibilitySettings
  authenticated: boolean
  children: React.ReactNode
}

function isDefault(settings: AccessibilitySettings): boolean {
  return JSON.stringify(settings) === JSON.stringify(DEFAULT_ACCESSIBILITY_SETTINGS)
}

export function AccessibilityProvider({ initialSettings, authenticated, children }: ProviderProps) {
  const [settings, setSettings] = useState<AccessibilitySettings>(initialSettings)
  const [announcement, setAnnouncement] = useState('')
  const skipNextPersistRef = useRef(true)

  // Pri montiranju usaglasi stanje sa izvorom istine.
  useEffect(() => {
    if (isDefault(initialSettings)) {
      const stored =
        typeof window !== 'undefined' && window.localStorage.getItem(A11Y_STORAGE_KEY) !== null
          ? readSettingsFromStorage()
          : null
      if (stored && !isDefault(stored)) {
        setSettings(stored)
        return
      }
      const preferences = detectPreferredSettings()
      if (preferences) {
        setSettings({ ...initialSettings, ...preferences })
      }
    }
  }, [authenticated, initialSettings])

  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false
      return
    }
    writeSettingsToStorage(settings)
    // Cookie se piše uvek — i anonimnima — da SSR odmah renderuje pravi prikaz
    // umesto podrazumevanog. Sama akcija preskoči Strapi kad nema sesije.
    void saveA11yCookie(settings).catch(() => {})
  }, [settings])

  // Primeni promene na <html>
  useEffect(() => {
    if (typeof document !== 'undefined') {
      applySettingsToElement(document.documentElement, settings)
    }
  }, [settings])

  const update = useCallback((partial: Partial<AccessibilitySettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }))
  }, [])

  const reset = useCallback(() => {
    setSettings(DEFAULT_ACCESSIBILITY_SETTINGS)
  }, [])

  const announce = useCallback((message: string) => {
    setAnnouncement('')
    setTimeout(() => setAnnouncement(message), 50)
  }, [])

  const value = useMemo<AccessibilityContextValue>(
    () => ({ settings, update, reset, announce, authenticated }),
    [settings, update, reset, announce, authenticated],
  )

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </AccessibilityContext.Provider>
  )
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext)
  if (!ctx) {
    throw new Error('useAccessibility mora biti unutar <AccessibilityProvider>')
  }
  return ctx
}
